import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { formatMoney } from 'lib/functions/utils';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { useDispatch } from 'react-redux';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import RatesCollapse from './RatesCollapse';
import {
  calculateRiskPremium,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';

interface DetailedRiskPremiumCalculation {
  amount: number;
  days: number;
  conversionRate: number;
  type: string;
  businessIdentifier: string | null;
  rateControls: RiskPremiumRates;
  setRateControls: React.Dispatch<React.SetStateAction<RiskPremiumRates>>;
  useConcentrationRates: boolean;
  canEditRates: boolean;
}

const useCreditLineData = (businessIdentifier: string | null) => {
  const dispatch = useDispatch();
  const [subProductsData, setSubProductsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditLineData = async () => {
    try {
      setIsLoading(true);
      if (!businessIdentifier) return;
      const creditLine = await getCreditLineByProduct(
        dispatch,
        businessIdentifier,
        'sgr',
      );
      const data = creditLine?.subProducts || creditLine || [];
      setSubProductsData(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  return {
    subProductsData,
    isLoading,
    error,
    fetchCreditLineData,
  };
};

const DetailedRiskPremiumCalculator: React.FC<
  DetailedRiskPremiumCalculation
> = ({
  amount,
  days,
  conversionRate,
  type,
  businessIdentifier,
  rateControls,
  setRateControls,
  useConcentrationRates,
  canEditRates = false,
}) => {
  const { subProductsData, isLoading, fetchCreditLineData } =
    useCreditLineData(businessIdentifier);

  const [showRateControls, setShowRateControls] = useState(false);

  useEffect(() => {
    fetchCreditLineData();
  }, [businessIdentifier]);

  useEffect(() => {
    if (!useConcentrationRates) return;
    if (subProductsData.length > 0 && type) {
      const subProduct = subProductsData.find(
        (sp: any) => sp.subProductId === type,
      );
      setRateControls((prev) => ({
        ...prev,
        variableRate: subProduct?.operationCostVariable ?? undefined,
        fixedRate: subProduct?.operationCostFixed ?? undefined,
        variableDiscount: subProduct?.discountRateVariable ?? undefined,
        fixedDiscount: subProduct?.discountRateFixed ?? undefined,
      }));
    }
  }, [subProductsData, type, setRateControls, useConcentrationRates]);

  const isValidCalculation =
    amount > 0 && days > 0 && conversionRate > 0 && type && type.trim() !== '';

  const calculationBreakdown = calculateRiskPremium({
    amount,
    days,
    conversionRate,
    type,
    rates: rateControls,
    subProductsData,
  });

  const totalPremium = calculationBreakdown?.totalPremium || 0;

  const handleRateChange = (field: keyof RiskPremiumRates, value: number) => {
    setRateControls((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <CalculateIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Cálculo Detallado de Prima de Riesgo
          </Typography>
          <Chip
            label="SGR"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>
        {canEditRates && (
          <IconButton
            size="small"
            onClick={() => setShowRateControls(!showRateControls)}
            sx={{ color: 'primary.main' }}
          >
            <SettingsIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        )}
        {totalPremium > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalPremium)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>

      <RatesCollapse
        show={showRateControls}
        rateControls={rateControls}
        onRateChange={handleRateChange}
        subProductsData={subProductsData}
        type={type}
      />

      <Box sx={{ textAlign: 'center', my: 2 }}>
        {!isValidCalculation && (
          <Box>
            <WarningIcon
              sx={{ fontSize: '2rem', color: 'text.disabled', mb: 0.75 }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                lineHeight: 1.2,
              }}
            >
              Complete todos los campos requeridos
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
            >
              Monto, días, tipo de garantía y tasa de conversión
            </Typography>
          </Box>
        )}
      </Box>

      {/* Calculation Breakdown */}
      {calculationBreakdown && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Monto en CLP:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationBreakdown.amountInCLP)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Monto garantía:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationBreakdown.guaranteeAmount)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Divider sx={{ my: 0.5 }} />
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa variable original:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationBreakdown.originalVariableRate.toFixed(2)}%
                  </Typography>
                </Stack>
              </Grid>
              {calculationBreakdown.variableDiscount > 0 && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descuento variable:
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      -{calculationBreakdown.variableDiscount.toFixed(2)}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa variable efectiva:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationBreakdown.effectiveVariableRate.toFixed(2)}%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Componente variable:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationBreakdown.variableComponent)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa fija original:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationBreakdown.originalFixedRate)} CLP
                  </Typography>
                </Stack>
              </Grid>
              {calculationBreakdown.fixedDiscount > 0 && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descuento fijo:
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      -{calculationBreakdown.fixedDiscount.toFixed(2)}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa fija efectiva:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationBreakdown.effectiveFixedRate)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Divider sx={{ my: 0.5 }} />
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" fontWeight={700} color="primary">
                    Prima Total:
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="primary">
                    {formatMoney(calculationBreakdown.totalPremium)} CLP
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
      {canEditRates && (
        <Alert
          severity="info"
          icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
          sx={{
            borderRadius: 0.5,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            border: '1px solid rgba(25, 118, 210, 0.2)',
            py: 0.75,
            '& .MuiAlert-message': { py: 0.25 },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
            Información Importante
          </AlertTitle>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
          >
            Las tasas mostradas pueden ser modificadas usando el botón de
            configuración.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default DetailedRiskPremiumCalculator;
